import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import AxiosInstance from 'src/components/Axios';
import {
	CForm, CFormLabel, CFormInput, CButton, CRow, CCol,
	CCard, CCardHeader, CCardBody, CFormSelect, CFormCheck,
	CInputGroup,
} from '@coreui/react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useCategoryScopes, useInactiveCategoryScopes } from 'src/hooks/useCategoryScopes';
import { CenterLoader } from 'src/components/myComponents/CenterLoader';
import FormSubmitOverlay from 'src/components/myComponents/FormSubmitOverlay/FormSubmitOverlay';
import { useSlugField } from 'src/hooks/useSlugField';
import { slugify } from 'src/utils/slug';
import { invalidateSupportCategories } from 'src/hooks/useSupportCategories';

const urlApi = '/support-categories';
const urlRouter = '/support-categories';

const SupportCategoriesForm = ({ user_data }) => {
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const isCreateMode = !id;

	const scopeOptions = useCategoryScopes();
	const allInactiveScopes = useInactiveCategoryScopes();
	const [inactiveAttachedScopes, setInactiveAttachedScopes] = useState([]);
	const [isEdit, setIsEdit] = useState(isCreateMode || location.pathname.includes('/update/'));

	useEffect(() => {
		setIsEdit(isCreateMode || location.pathname.includes('/update/'));
	}, [location.pathname, isCreateMode]);
	const [data, setData] = useState(null);
	const [name, setName] = useState('');
	const [scopes, setScopes] = useState(['knowledge_base']);
	const [isActive, setIsActive] = useState(true);
	const [sortOrder, setSortOrder] = useState(0);
	const [loading, setLoading] = useState(!!id);
	const [submitting, setSubmitting] = useState(false);
	const [errors, setErrors] = useState({});
	const {
		slug, setSlug,
		slugManuallyEdited, setSlugManuallyEdited,
		handleSlugChange: slugHandleChange,
		handleSlugPaste: slugHandlePaste,
		autoSlugFromName,
	} = useSlugField();

	useEffect(() => {
		if (!data?.scopes?.length || !scopeOptions.length) return;
		const activeCodesSet = new Set(scopeOptions.map(o => o.code));
		const missingCodes = data.scopes.filter(code => !activeCodesSet.has(code));
		if (!missingCodes.length) {
			setInactiveAttachedScopes([]);
			return;
		}
		setInactiveAttachedScopes(
			allInactiveScopes.filter(s => missingCodes.includes(s.code))
		);
	}, [data, scopeOptions, allInactiveScopes]);

	useEffect(() => {
		if (!id) return;
		setLoading(true);
		AxiosInstance.get(`${urlApi}/${id}`)
			.then(res => {
				const item = res.data;
				setData(item);
				setName(item.name || '');
				setSlug(item.slug || '');
				setScopes(item.scopes?.length ? item.scopes : ['knowledge_base']);
				setIsActive(item.is_active ?? true);
				setSortOrder(item.sort_order ?? 0);
				setSlugManuallyEdited(true);
			})
			.catch(() => toast.error('Помилка завантаження категорії'))
			.finally(() => setLoading(false));
	}, [id]);

	const hasChanges = !isCreateMode && data !== null && (
		name !== (data.name || '') ||
		slug !== (data.slug || '') ||
		isActive !== (data.is_active ?? true) ||
		Number(sortOrder) !== (data.sort_order ?? 0) ||
		[...scopes].sort().join(',') !== [...(data.scopes || [])].sort().join(',')
	);

	const handleReset = useCallback(() => {
		if (!data) return;
		setName(data.name || '');
		setSlug(data.slug || '');
		setScopes(data.scopes?.length ? data.scopes : ['knowledge_base']);
		setIsActive(data.is_active ?? true);
		setSortOrder(data.sort_order ?? 0);
		setErrors({});
	}, [data]);

	const handleBack = useCallback(() => navigate(-1), [navigate]);

	const handleNameChange = (e) => {
		const val = e.target.value;
		setName(val);
		if (errors.name) setErrors(prev => ({ ...prev, name: false }));
		if (!slugManuallyEdited) {
			setSlug(slugify(val));
		}
	};

	const handleSlugChange = (e) => {
		slugHandleChange(e);
		if (errors.slug) setErrors(prev => ({ ...prev, slug: false }));
	};

	const handleSlugPaste = (e) => {
		slugHandlePaste(e);
		if (errors.slug) setErrors(prev => ({ ...prev, slug: false }));
	};

	const handleAutoSlug = () => {
		autoSlugFromName(name);
		if (errors.slug) setErrors(prev => ({ ...prev, slug: false }));
	};

	const handleScopeToggle = (value, checked) => {
		setScopes(prev => {
			const next = checked ? [...prev, value] : prev.filter(s => s !== value);
			if (next.length > 0 && errors.scopes) setErrors(e => ({ ...e, scopes: false }));
			return next;
		});
	};

	const handleSubmit = async () => {
		const newErrors = {
			name: !name.trim(),
			slug: !slug.trim(),
			scopes: scopes.length === 0,
		};
		if (newErrors.name || newErrors.slug || newErrors.scopes) {
			setErrors(newErrors);
			const missing = [
				newErrors.name && 'Назва',
				newErrors.slug && 'Slug',
				newErrors.scopes && 'Область застосування',
			].filter(Boolean);
			toast.warning(`Не заповнено: ${missing.join(', ')}`);
			return;
		}
		setErrors({});

		const payload = {
			name: name.trim(),
			slug: slug.trim(),
			scopes,
			is_active: isActive,
			sort_order: Number(sortOrder),
		};

		if (isCreateMode) {
			try {
				const res = await AxiosInstance.post(urlApi, payload);
				invalidateSupportCategories();
				toast.success('Категорію створено!');
				navigate(`${urlRouter}/view/${res.data.id}`, { replace: true });
			} catch (err) {
				toast.error(err.response?.data?.error || 'Помилка збереження');
			}
			return;
		}

		setSubmitting(true);
		try {
			const res = await AxiosInstance.put(`${urlApi}/${id}`, payload);
			const item = res.data;
			setData(item);
			invalidateSupportCategories();
			setName(item.name || '');
			setSlug(item.slug || '');
			setScopes(item.scopes?.length ? item.scopes : ['knowledge_base']);
			setIsActive(item.is_active ?? true);
			setSortOrder(item.sort_order ?? 0);
			toast.success('Категорію оновлено!');
		} catch (err) {
			toast.error(err.response?.data?.error || 'Помилка збереження');
		} finally {
			setSubmitting(false);
		}
	};

	const viewFields = data ? [
		{ key: 'id', label: 'ID', value: String(data.id) },
		{ key: 'name', label: 'Назва', value: data.name || '' },
		{ key: 'slug', label: 'Slug', value: data.slug || '' },
		{
			key: 'scopes', label: 'Область застосування категорій', render: () => (
				<div className="form-control" style={{ background: '#e9ecef', minHeight: '38px' }}>
					{(data.scopes || []).map((s, i) => {
						const active = scopeOptions.find(o => o.code === s);
						if (active) return <React.Fragment key={s}>{i > 0 && ', '}{active.name}</React.Fragment>;
						const inactive = inactiveAttachedScopes.find(o => o.code === s);
						return (
							<React.Fragment key={s}>
								{i > 0 && ', '}
								<s>{inactive?.name || s}</s>{' '}(неактивний)
							</React.Fragment>
						);
					})}
				</div>
			),
		},
		{ key: 'sort_order', label: 'Порядок сортування', value: String(data.sort_order ?? 0) },
		{ key: 'is_active', label: 'Активна', value: data.is_active ? 'Так' : 'Ні' },
	] : [];

	const cardTitle = isCreateMode
		? 'Нова категорія підтримки'
		: (isEdit ? 'Категорія підтримки — редагування' : 'Категорія підтримки — перегляд');

	return (
		<>
			<ToastContainer />
			<CCard className="mb-4">
				<CCardHeader>
					<strong>{cardTitle}</strong>
				</CCardHeader>
				<CCardBody>
					{loading ? (
						<CenterLoader />
					) : (
						<FormSubmitOverlay submitting={submitting}>
							<CForm className="mb-3">
								{isEdit ? (
									<CRow className="g-3">
										<CCol md={6}>
											<CFormLabel>Назва *</CFormLabel>
											<CFormInput
												value={name}
												onChange={handleNameChange}
												placeholder="Наприклад: Технічні питання"
												autoComplete="off"
												invalid={!!errors.name}
											/>
										</CCol>
										<CCol md={6}>
											<CFormLabel>Slug *</CFormLabel>
											<CInputGroup className={errors.slug ? 'is-invalid' : ''}>
												<CButton
													color="secondary"
													variant="outline"
													onClick={handleAutoSlug}
													title="Згенерувати slug з назви"
													type="button"
												>
													Аз→az
												</CButton>
												<CFormInput
													value={slug}
													onChange={handleSlugChange}
													onPaste={handleSlugPaste}
													placeholder="tekhnichni-pytannia"
													autoComplete="off"
													invalid={!!errors.slug}
												/>
											</CInputGroup>
										</CCol>
										<CCol md={6}>
											<CFormLabel>Область застосування категорій *</CFormLabel>
											<div className="d-flex gap-3 flex-wrap mt-1">
												{scopeOptions.map(o => (
													<CFormCheck
														key={o.code}
														id={`scope-${o.code}`}
														label={o.name}
														checked={scopes.includes(o.code)}
														onChange={(e) => handleScopeToggle(o.code, e.target.checked)}
													/>
												))}
												{inactiveAttachedScopes.map(o => (
													<CFormCheck
														key={o.code}
														id={`scope-inactive-${o.code}`}
														label={<><s>{o.name}</s> (неактивний)</>}
														checked
														disabled
													/>
												))}
											</div>
											{errors.scopes && (
												<div className="text-danger small mt-1">
													Оберіть мінімум одну область застосування
												</div>
											)}
										</CCol>
										<CCol md={2}>
											<CFormLabel>Порядок сортування</CFormLabel>
											<CFormInput
												type="number"
												value={sortOrder}
												onChange={(e) => setSortOrder(e.target.value)}
											/>
										</CCol>
										<CCol md={2} className="d-flex align-items-end pb-1">
											<span title="Визначає, чи доступна ця категорія для вибору при роботі з Q&A, листами, FAQ. Неактивна категорія прихована з усіх селектів.">
												<CFormCheck
													id="isActiveCheck"
													label="Активна"
													checked={isActive}
													onChange={(e) => setIsActive(e.target.checked)}
												/>
											</span>
										</CCol>
									</CRow>
								) : (
									<CRow className="g-3">
										{viewFields.map(({ key, label, value, render }) => (
											<CCol md={6} key={key}>
												<CFormLabel>{label}</CFormLabel>
												{render ? render() : <CFormInput type="text" value={value} disabled readOnly />}
											</CCol>
										))}
									</CRow>
								)}

								<div className="d-flex justify-content-start mt-4">
									<CButton color="secondary" variant="outline" onClick={handleBack} title="Повернутись до попередньої сторінки">
										← Назад
									</CButton>
									{!isEdit && (
										<CButton color="success" className="ms-2" onClick={() => navigate(`${urlRouter}/update/${id}`)} title="Перейти до форми редагування">
											Редагувати
										</CButton>
									)}
									{isEdit && (
										<CButton
											color="success"
											className="ms-2"
											onClick={handleSubmit}
											disabled={!isCreateMode && !hasChanges}
											title={isCreateMode ? 'Створити нову категорію' : 'Зберегти зміни'}
										>
											{isCreateMode ? 'Створити' : 'Зберегти'}
										</CButton>
									)}
									{isEdit && !isCreateMode && (
										<CButton
											color="secondary"
											variant="outline"
											className="ms-2"
											onClick={handleReset}
											disabled={!hasChanges}
											title="Скинути форму до останніх збережених значень"
										>
											Скинути
										</CButton>
									)}
									{isEdit && !isCreateMode && (
										<CButton
											color="danger"
											variant="outline"
											className="ms-2"
											onClick={() => navigate(`${urlRouter}/view/${id}`)}
											title="Скасувати редагування і повернутись до перегляду"
										>
											До перегляду
										</CButton>
									)}
								</div>
							</CForm>
						</FormSubmitOverlay>
					)}
				</CCardBody>
			</CCard>
		</>
	);
};

export default SupportCategoriesForm;
